import { z } from "zod";

import { REDIS_KEY_TYPES } from "../../../../shared/domain/redis/key-types";

/**
 * Admin Redis Cache API Schemas
 *
 * Secure admin-only access to Redis cache inspection and management.
 */

// Query parameters for listing keys
export const AdminRedisCacheListKeysQuerySchema = z.object({
  pattern: z
    .string()
    .min(1)
    .max(100)
    .optional()
    .default("*")
    .describe("Redis pattern to match keys (e.g., 'user:*', 'session:*')"),
  limit: z.coerce
    .number()
    .int()
    .positive()
    .max(1000)
    .optional()
    .default(100)
    .describe("Maximum number of keys to return")
});

// Key with type information (content-aware)
export const RedisCacheKeySchema = z.object({
  key: z.string(),
  type: z.enum(REDIS_KEY_TYPES)
});

/**
 * List keys response.
 *
 * ## There is no `total`, and that is the point
 *
 * Redis has no count-matching-pattern primitive. Counting the keys a pattern
 * matches means a second full SCAN — O(keyspace) on every page load — and the
 * two scans still race, so the answer would be approximate even after paying
 * for it. A `total` here could therefore only ever be the page length, which
 * is what it was: `keys.length`, computed after the adapter's own cap, so
 * `total` was identically the number of keys in `keys` and the UI rendered
 * "500 / 500" against a Redis of any size.
 *
 * `truncated` is the fact the scan actually establishes, for free, and it is
 * exact. A number that is not exact is omitted rather than flagged: an absent
 * field cannot be misread as a count, where a wrong count reads exactly like a
 * right one.
 */
export const AdminRedisCacheListKeysResponseSchema = z.object({
  keys: z.array(RedisCacheKeySchema),
  truncated: z
    .boolean()
    .describe(
      "The SCAN stopped before exhausting the keyspace, so further keys may match this pattern. False means every matching key is in `keys`."
    ),
  pattern: z.string()
});

// Get key response (content-aware type detection)
export const AdminRedisCacheGetKeyResponseSchema = z.object({
  key: z.string(),
  type: z.enum(REDIS_KEY_TYPES),
  value: z.unknown().nullable(),
  ttl: z.number().describe("TTL in seconds, -1 for no expiry, -2 for key not found"),
  size: z.number().describe("Size in bytes")
});

// Delete key response
export const AdminRedisCacheDeleteKeyResponseSchema = z.object({
  deleted: z.boolean(),
  key: z.string()
});

// Flush all response
export const AdminRedisCacheFlushAllResponseSchema = z.object({
  flushed: z.number().describe("Number of keys deleted"),
  pattern: z.string().optional()
});

// Stats response
export const AdminRedisCacheStatsResponseSchema = z.object({
  totalKeys: z.number(),
  memoryUsed: z.number(),
  memoryPeak: z.number(),
  connectedClients: z.number(),
  commandsProcessed: z.number(),
  uptime: z.number()
});

// Path variables
export const AdminRedisCacheKeyPathVarsSchema = z.object({
  key: z.string().min(1).max(500)
});

// Flush query (optional pattern)
export const AdminRedisCacheFlushQuerySchema = z.object({
  pattern: z.string().optional().describe("Pattern to flush (default: *)"),
  confirm: z
    .string()
    .optional()
    .refine((val) => val === "DELETE_ALL_CACHE", {
      message: "Must confirm with 'DELETE_ALL_CACHE'"
    })
});

/**
 * Namespace analysis.
 *
 * Every figure here describes the SCANNED keys, never the keyspace: the
 * adapter groups a capped SCAN, so `keyCount` and `totalSize` are sums over a
 * sample whose size `truncated` discloses.
 *
 * ## `percentage` is gone
 *
 * It was `.describe("Percentage of total cache")` and it was never that. It was
 * computed as this namespace's bytes over the sum of the RETURNED namespaces'
 * bytes — an exact share of the sample, published under a label claiming a
 * share of the cache, so the shares always summed to 100% no matter how little
 * of Redis was scanned. It is also derivable from `totalSize`, so it carried no
 * information the client did not already hold; the only thing it added was the
 * false denominator. The UI computes the share of what is listed and says so.
 */
export const AdminRedisCacheNamespaceStatsSchema = z.object({
  prefix: z.string().describe("Namespace prefix (e.g., 'deployment', 'chat')"),
  keyCount: z.number().describe("Keys in this namespace AMONG THE SCANNED KEYS"),
  totalSize: z.number().describe("Size in bytes of this namespace's SCANNED keys"),
  formattedSize: z.string().describe("Human-readable size (e.g., '45.2 MB')"),
  averageKeySize: z.number().describe("Average size in bytes of this namespace's scanned keys"),
  sampleKeys: z.array(z.string()).describe("Sample keys from this namespace")
});

export const AdminRedisCacheAnalyzeNamespacesResponseSchema = z.object({
  namespaces: z.array(AdminRedisCacheNamespaceStatsSchema),
  truncated: z
    .boolean()
    .describe(
      "The SCAN behind this analysis stopped before exhausting the keyspace, so these namespaces and their sizes cover a sample of Redis rather than all of it."
    )
});

// Top consumers
export const AdminRedisCacheTopConsumerSchema = z.object({
  key: z.string().describe("Cache key name"),
  type: z.string().describe("Redis type (string, json, hash, list, etc.)"),
  size: z.number().describe("Size in bytes"),
  formattedSize: z.string().describe("Human-readable size (e.g., '12.4 MB')"),
  ttl: z.number().describe("TTL in seconds (-1 = no expiry)")
});

export const AdminRedisCacheGetTopConsumersQuerySchema = z.object({
  limit: z
    .number()
    .int()
    .positive()
    .max(100)
    .optional()
    .default(10)
    .describe("Number of top consumers to return (default: 10, max: 100)")
});

/**
 * Top consumers.
 *
 * `totalKeys` is gone. It was `topConsumers.length` — the requested `limit`,
 * never a count of Redis — under a name an operator reads as the size of the
 * keyspace, and the client already holds `topConsumers.length`.
 *
 * `truncated` is the load-bearing field: this is a ranking over a capped SCAN,
 * so when it is true THE LARGEST KEY IN REDIS MAY BE ABSENT FROM THIS LIST.
 * The ranking is kept rather than omitted because a ranking over a disclosed
 * sample is still the feature; a silent one is the defect.
 */
export const AdminRedisCacheGetTopConsumersResponseSchema = z.object({
  topConsumers: z.array(AdminRedisCacheTopConsumerSchema),
  truncated: z
    .boolean()
    .describe(
      "The SCAN this ranking was drawn from stopped before exhausting the keyspace. When true, keys larger than any listed here may exist."
    )
});

// Memory pressure
export const AdminRedisCacheMemoryPressureResponseSchema = z.object({
  usedMemory: z.number().describe("Memory used in bytes"),
  maxMemory: z.number().describe("Max memory limit in bytes (0 = unlimited)"),
  percentage: z.number().describe("Memory usage percentage"),
  fragmentationRatio: z.number().describe("Memory fragmentation ratio"),
  evictionPolicy: z.string().describe("Redis eviction policy (e.g., allkeys-lru, noeviction)"),
  evictedKeys: z.number().describe("Total keys evicted since server start"),
  status: z.enum(["healthy", "warning", "critical"]).describe("Health status"),
  recommendation: z.string().nullable().describe("Actionable recommendation (null if all healthy)")
});

// Idle keys analysis
export const IdleKeyBucketSchema = z.object({
  count: z.number().describe("Number of keys in this bucket"),
  totalSize: z.number().describe("Total size in bytes"),
  sampleKeys: z.array(z.string()).describe("Sample keys from this bucket")
});

/**
 * Idle-key analysis.
 *
 * The buckets partition a capped SCAN, not the keyspace, so every `count` and
 * `totalSize` is a sum over a sample that `truncated` discloses. The buckets
 * are kept rather than omitted because they ARE the feature and a bucket over a
 * disclosed sample is honest; what was wrong was that nothing disclosed it.
 *
 * `recommendation` survives untouched, and deliberately: it says "you can free
 * up N MB", which UNDER-states on a truncated scan. A lower bound stays true as
 * the sample grows, so it is the one figure here a cap cannot falsify.
 */
export const AdminRedisCacheIdleAnalysisResponseSchema = z.object({
  veryStale: IdleKeyBucketSchema.describe("Scanned keys idle for 30+ days (safe to delete)"),
  stale: IdleKeyBucketSchema.describe("Scanned keys idle for 7-30 days"),
  aging: IdleKeyBucketSchema.describe("Scanned keys idle for 1-7 days"),
  active: IdleKeyBucketSchema.describe("Scanned keys idle for < 1 day"),
  truncated: z
    .boolean()
    .describe(
      "The SCAN behind these buckets stopped before exhausting the keyspace, so the counts and sizes cover a sample of Redis rather than all of it."
    ),
  recommendation: z
    .string()
    .nullable()
    .describe("Cleanup recommendation (null if no cleanup needed)")
});
